import {
  Ban,
  CircleAlert,
  CircleCheck,
  Clock,
  FileWarning,
  PauseCircle,
  RefreshCw,
  RotateCcw,
  Send,
  ShieldQuestion,
  UsersRound,
  type LucideIcon,
} from "lucide-react";
import { useCallback, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";

import { useAuth } from "@/modules/auth/hooks/useAuth";
import { OrganizationScopePicker } from "@/modules/dashboard/payments-config/components/OrganizationScopePicker";
import { useOrganizationsCatalog } from "@/modules/dashboard/payments-config/hooks/useOrganizationsCatalog";
import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaIconButton } from "@/shared/components/buttons/ColocaIconButton";
import { ColocaKpiCard } from "@/shared/components/data-display/ColocaKpiCard";
import { ColocaAlert } from "@/shared/components/feedback/ColocaAlert";
import { ColocaAlertDialog } from "@/shared/components/feedback/ColocaAlertDialog";
import { ColocaEmpty } from "@/shared/components/feedback/ColocaEmpty";
import { ColocaSearchInput } from "@/shared/components/forms/ColocaSearchInput";
import { ColocaPageHeader } from "@/shared/components/layout/ColocaPageHeader";
import { useAppShellBreadcrumb } from "@/shared/components/layout/useAppShellBreadcrumb";
import { ColocaPagination } from "@/shared/components/navigation/ColocaPagination";
import { useTranslations } from "@/shared/lang/useTranslations";
import { cn } from "@/shared/lib/utils";
import { colocaToast } from "@/shared/utils/toast";

import { CardholderBoardTable } from "../components/CardholderBoardTable";
import { CardholderDetailDrawer } from "../components/CardholderDetailDrawer";
import { LinkVerificationDialog } from "../components/LinkVerificationDialog";
import { SendToProviderDialog } from "../components/SendToProviderDialog";
import { SyncVerificationDialog } from "../components/SyncVerificationDialog";
import {
  CARDHOLDER_BOARD_PAGE_SIZE,
  CARDHOLDER_ONBOARDING_ROUTE,
} from "../constants/cardholderOnboarding.constants";
import { useCardholderBoard } from "../hooks/useCardholderBoard";
import { useCardholderCopy } from "../hooks/useCardholderCopy";
import { useRetryRegistration } from "../hooks/useRetryRegistration";
import type { CardholderBoardItem, CardholderBucket } from "../types/cardholderOnboarding.types";
import { BUCKET_ACCENT, BUCKET_LABEL_KEYS, BUCKET_ORDER, isSelectableRow } from "../utils/cardholderBoard";

const ORGANIZATION_PARAM = "org";

const BUCKET_ICONS: Record<CardholderBucket, LucideIcon> = {
  ready: Send,
  paused: PauseCircle,
  needs_verification: ShieldQuestion,
  needs_data: FileWarning,
  registration_failed: RotateCcw,
  in_progress: Clock,
  enabled: CircleCheck,
  closed: Ban,
};

type DialogState =
  | { kind: "send"; items: CardholderBoardItem[] }
  | { kind: "link" | "sync" | "data" | "retry"; item: CardholderBoardItem }
  | null;

/**
 * `/app/cardholder-onboarding` — INTERNAL staff. One organization's cardholders, each with the step it is
 * held at (registration, verification, card provider) and the one action that moves it forward. Reads `GET
 * /internal/identities/cardholder-onboarding`; every action is one of the existing staff endpoints, run as a
 * dry run first wherever the endpoint has one.
 */
const CardholderOnboardingPage = () => {
  const { t } = useTranslations("cardholderOnboarding");
  const { t: tGlobal } = useTranslations("global");
  const { can } = useAuth();
  const copy = useCardholderCopy();
  const catalog = useOrganizationsCatalog();
  const [searchParams, setSearchParams] = useSearchParams();
  const organizationId = searchParams.get(ORGANIZATION_PARAM);
  const board = useCardholderBoard(organizationId);
  const { retry, retryingId } = useRetryRegistration(organizationId);
  const [detail, setDetail] = useState<CardholderBoardItem | null>(null);
  const [dialog, setDialog] = useState<DialogState>(null);
  // A selection belongs to the page of rows it was made on.
  const selectionKey = `${organizationId}|${board.bucket}|${board.search}|${board.page}`;
  const [selection, setSelection] = useState<{ key: string; ids: Set<string> }>({
    key: selectionKey,
    ids: new Set(),
  });
  const pickedIds = selection.key === selectionKey ? selection.ids : null;

  const breadcrumb = useMemo(
    () => [
      { label: tGlobal("sidebar_section_gestion") },
      { label: tGlobal("sidebar_menu_cardholder_onboarding"), to: CARDHOLDER_ONBOARDING_ROUTE },
    ],
    [tGlobal],
  );
  useAppShellBreadcrumb(breadcrumb);

  const scopeLabels = useMemo(
    () => ({
      label: t("scope_label"),
      placeholder: t("scope_placeholder"),
      loading: t("scope_loading"),
      empty: t("scope_empty"),
      searching: t("scope_searching"),
      searchError: t("scope_search_error"),
      catalogError: t("scope_catalog_error"),
      catalogTruncated: t("scope_catalog_truncated"),
      requiredHint: t("scope_required_hint"),
      orgHint: t("scope_org_hint"),
    }),
    [t],
  );

  const data = board.board;
  const rows = data?.data ?? [];
  const canWrite = can("identities:write");
  const canQueue = canWrite && !!data?.organization.onboarding_supported;
  // A reload can move a picked row to another bucket or page, so the selection is always read off the rows on
  // screen that can still be sent.
  const selectedItems = pickedIds
    ? rows.filter((row) => pickedIds.has(row.identity_id) && isSelectableRow(row, canQueue))
    : [];
  const selected = new Set(selectedItems.map((row) => row.identity_id));
  const providerLabel = copy.providerLabel(data?.organization.card_provider);

  const setOrganization = (next: string | null) => {
    const params = new URLSearchParams(searchParams);
    if (next) params.set(ORGANIZATION_PARAM, next);
    else params.delete(ORGANIZATION_PARAM);
    setSearchParams(params, { replace: true });
    setDetail(null);
    setDialog(null);
  };

  const toggle = (identityId: string, checked: boolean) => {
    const ids = new Set(selected);
    if (checked) ids.add(identityId);
    else ids.delete(identityId);
    setSelection({ key: selectionKey, ids });
  };

  const togglePage = (checked: boolean) => {
    const ids = new Set(selected);
    for (const row of rows) {
      if (!isSelectableRow(row, canQueue)) continue;
      if (checked) ids.add(row.identity_id);
      else ids.delete(row.identity_id);
    }
    setSelection({ key: selectionKey, ids });
  };

  const clearSelection = () => setSelection({ key: selectionKey, ids: new Set() });

  const openAction = useCallback((item: CardholderBoardItem) => {
    setDetail(null);
    switch (item.next_action) {
      case "send":
      case "resume":
        setDialog({ kind: "send", items: [item] });
        break;
      case "link_verification":
        setDialog({ kind: "link", item });
        break;
      case "sync_verification":
        setDialog({ kind: "sync", item });
        break;
      case "complete_data":
        setDialog({ kind: "data", item });
        break;
      case "retry_registration":
        setDialog({ kind: "retry", item });
        break;
      default:
        break;
    }
  }, []);

  const afterChange = () => {
    clearSelection();
    void board.reload();
  };

  const handleRetry = async (item: CardholderBoardItem) => {
    setDialog(null);
    const outcome = await retry(item.identity_id, t("error_generic"));
    if (outcome.ok) colocaToast.success(t("retry_done", { name: item.name ?? t("unnamed") }));
    else colocaToast.error(outcome.message);
    void board.reload();
  };

  const term = board.search.trim();

  return (
    <div className="space-y-6 p-2">
      <ColocaPageHeader
        title={t("page_title")}
        description={t("page_subtitle")}
        actions={
          organizationId ? (
            <ColocaIconButton
              icon={RefreshCw}
              variant="ghost"
              size="sm"
              aria-label={t("action_refresh")}
              className={cn(board.loading && "[&_svg]:animate-spin")}
              onClick={() => void board.reload()}
            />
          ) : undefined
        }
      />

      <div className="space-y-2">
        <OrganizationScopePicker
          id="cardholder-onboarding-scope"
          className="max-w-md"
          mode="required"
          value={organizationId}
          onValueChange={setOrganization}
          catalog={catalog}
          labels={scopeLabels}
        />
        {data && (
          <p className="text-sm text-muted-foreground">
            {t("summary_line", {
              name: data.organization.name,
              provider: providerLabel,
              count: data.counts.total,
            })}
          </p>
        )}
      </div>

      {!organizationId ? (
        <ColocaEmpty
          surface="card"
          icon={UsersRound}
          title={t("pick_org_title")}
          description={t("pick_org_description")}
        />
      ) : board.error ? (
        <ColocaAlert
          variant="destructive"
          icon={CircleAlert}
          text={board.error === "denied" ? t("denied_error") : t("load_error")}
          actions={
            board.error === "failed" ? (
              <ColocaButton size="sm" variant="default" icon={RefreshCw} onClick={() => void board.reload()}>
                {t("retry")}
              </ColocaButton>
            ) : undefined
          }
        />
      ) : (
        <>
          {data && !data.organization.onboarding_supported && (
            <ColocaAlert
              variant="secondary"
              icon={CircleAlert}
              text={t("unsupported_notice", { provider: providerLabel })}
            />
          )}
          {data?.truncated && (
            <ColocaAlert variant="warning" icon={CircleAlert} text={t("truncated_notice")} />
          )}

          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            {BUCKET_ORDER.map((bucket) => (
              <ColocaKpiCard
                key={bucket}
                label={t(BUCKET_LABEL_KEYS[bucket])}
                value={data ? data.counts[bucket] : "—"}
                icon={BUCKET_ICONS[bucket]}
                accent={BUCKET_ACCENT[bucket]}
                selected={board.bucket === bucket}
                onSelect={() => board.setBucket(board.bucket === bucket ? null : bucket)}
              />
            ))}
          </div>

          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <ColocaSearchInput
              value={board.search}
              onValueChange={board.setSearch}
              placeholder={t("search_placeholder")}
              aria-label={t("search_placeholder")}
              className="w-full sm:max-w-sm"
            />
            {selected.size > 0 && (
              <div className="flex flex-col items-stretch gap-2 sm:flex-row sm:items-center">
                <span className="text-sm text-muted-foreground">
                  {t("bulk_selected", { count: selected.size })}
                </span>
                <ColocaButton variant="ghost" className="w-full sm:w-auto" onClick={clearSelection}>
                  {t("bulk_clear")}
                </ColocaButton>
                <ColocaButton
                  variant="primary"
                  icon={Send}
                  className="w-full sm:w-auto"
                  onClick={() => setDialog({ kind: "send", items: selectedItems })}
                >
                  {t("bulk_send", { provider: providerLabel })}
                </ColocaButton>
              </div>
            )}
          </div>

          <CardholderBoardTable
            rows={rows}
            loading={board.loading && !data}
            providerLabel={providerLabel}
            canQueue={canQueue}
            canWrite={canWrite}
            selected={selected}
            onToggle={toggle}
            onTogglePage={togglePage}
            onOpen={setDetail}
            onAction={openAction}
            empty={
              <ColocaEmpty
                surface="card"
                icon={UsersRound}
                title={
                  term
                    ? t("empty_search", { term })
                    : board.bucket
                      ? t("empty_bucket")
                      : t("empty_organization")
                }
              >
                {(term || board.bucket) && (
                  <ColocaButton
                    variant="default"
                    onClick={() => {
                      board.setSearch("");
                      board.setBucket(null);
                    }}
                  >
                    {t("clear_filters")}
                  </ColocaButton>
                )}
              </ColocaEmpty>
            }
            footer={
              data && data.total > CARDHOLDER_BOARD_PAGE_SIZE ? (
                <ColocaPagination
                  page={board.page}
                  perPage={CARDHOLDER_BOARD_PAGE_SIZE}
                  total={data.total}
                  withSeparator={false}
                  onPageChange={board.setPage}
                />
              ) : undefined
            }
          />
        </>
      )}

      <CardholderDetailDrawer
        item={detail}
        providerLabel={providerLabel}
        canQueue={canQueue}
        canWrite={canWrite}
        onOpenChange={(open) => {
          if (!open) setDetail(null);
        }}
        onAction={openAction}
      />

      {organizationId && dialog?.kind === "send" && (
        <SendToProviderDialog
          organizationId={organizationId}
          items={dialog.items}
          providerLabel={providerLabel}
          onClose={() => setDialog(null)}
          onSent={afterChange}
        />
      )}
      {dialog?.kind === "link" && (
        <LinkVerificationDialog
          item={dialog.item}
          providerLabel={providerLabel}
          onClose={() => setDialog(null)}
          onLinked={afterChange}
        />
      )}
      {dialog?.kind === "sync" && (
        <SyncVerificationDialog
          item={dialog.item}
          onClose={() => setDialog(null)}
          onSynced={afterChange}
          onLinkInstead={() => setDialog({ kind: "link", item: dialog.item })}
        />
      )}

      <ColocaAlertDialog
        open={dialog?.kind === "data"}
        onOpenChange={(open) => {
          if (!open) setDialog(null);
        }}
        title={t("data_title")}
        description={
          dialog?.kind === "data"
            ? dialog.item.blocked_reason === "missing_client_email"
              ? t("data_missing_email", { name: dialog.item.name ?? t("unnamed") })
              : t("data_missing_identity_fields", { name: dialog.item.name ?? t("unnamed") })
            : undefined
        }
        confirmLabel={t("data_ok")}
        cancelLabel={null}
        onConfirm={() => setDialog(null)}
      />

      <ColocaAlertDialog
        open={dialog?.kind === "retry"}
        onOpenChange={(open) => {
          if (!open) setDialog(null);
        }}
        title={t("retry_title")}
        description={
          dialog?.kind === "retry"
            ? t("retry_description", { name: dialog.item.name ?? t("unnamed"), provider: providerLabel })
            : undefined
        }
        confirmLabel={t("action_retry_registration")}
        cancelLabel={t("cancel")}
        confirmDisabled={retryingId !== null}
        onConfirm={() => {
          if (dialog?.kind === "retry") void handleRetry(dialog.item);
        }}
      />
    </div>
  );
};

export default CardholderOnboardingPage;
